import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../firebase.init";
import axios from "axios";

const NavBar = () => {
  const [user] = useAuthState(auth);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Cache keys for localStorage
  const CACHE_KEYS = {
    AUTH_STATE: "app_auth_state",
    USER_DATA: "app_user_data",
  };

  const [logoData, setLogoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLogoData = async () => {
      try {
        const response = await axios.get("https://ecom-22-fe6311cae608.herokuapp.com/api/nav-logo");
        setLogoData(response.data.data);
      } catch (err) {
        setError("Failed to load logo");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogoData();
  }, []);

  // Handle Firebase auth state changes
  useEffect(() => {
    if (!loading) {
      setAuthLoading(false);
      // Cache auth state
      try {
        const authStateData = {
          hasUser: !!user,
          timestamp: Date.now(),
          userEmail: user?.email || null,
        };
        localStorage.setItem(
          CACHE_KEYS.AUTH_STATE,
          JSON.stringify(authStateData)
        );
      } catch (error) {
        console.error("Error caching auth state:", error);
      }
    }
  }, [user, loading]);

  const handleSignout = async () => {
    try {
      await signOut(auth);
      // Clear all cached data on logout
      Object.values(CACHE_KEYS).forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error clearing ${key} from localStorage:`, error);
        }
      });
      setUserData(null);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
    // Close mobile menu when opening user dropdown
    if (mobileMenuVisible) {
      setMobileMenuVisible(false);
    }
  };

  const closeUserDropdown = () => {
    setUserDropdownOpen(false);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible);
    // Close user dropdown when opening mobile menu
    if (userDropdownOpen) {
      setUserDropdownOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close user dropdown if clicking outside
      if (
        userDropdownOpen &&
        !event.target.closest(".user-dropdown-container") &&
        !event.target.closest(".user-avatar-button")
      ) {
        closeUserDropdown();
      }

      // Close mobile menu if clicking outside
      if (
        mobileMenuVisible &&
        !event.target.closest(".mobile-menu") &&
        !event.target.closest(".mobile-menu-button")
      ) {
        setMobileMenuVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userDropdownOpen, mobileMenuVisible]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(" ")
        .map((name) => name[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    }
    return user?.email?.substring(0, 2).toUpperCase() || "U";
  };

  // Fetch user data with caching
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) {
        setUserData(null);
        // Clear cached user data when no user
        try {
          localStorage.removeItem(CACHE_KEYS.USER_DATA);
        } catch (error) {
          console.error("Error clearing cached user data:", error);
        }
        return;
      }
      try {
        const response = await fetch(
          `https://ecom-22-fe6311cae608.herokuapp.com/users?userEmail=${user.email}`
        );
        const data = await response.json();
        if (data && data.length > 0) {
          const userData = data[0];
          setUserData(userData);
          // Cache user data
          try {
            localStorage.setItem(
              CACHE_KEYS.USER_DATA,
              JSON.stringify(userData)
            );
          } catch (error) {
            console.error("Error caching user data:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    // Only fetch user data if we have a user and either no cached data or cached data is stale
    if (user?.email) {
      const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
      const shouldFetch = !cachedUserData || !userData;
      if (shouldFetch) {
        fetchUserData();
      }
    }
  }, [user]);

  // Render logo with proper null checks
  const renderLogo = (className = "") => {
    if (loading) {
      return <div className={`logo-placeholder ${className}`}>Loading...</div>;
    }
    if (error || !logoData || !logoData.isActive) {
      // Fallback to default logo
      return (
        <img
          src="https://attherate.dev/host-image/uploads/1760109986_ProductimgAI.png"
          alt="logo-img"
          className={className}
        />
      );
    }
    return (
      <img
        src={logoData.imageUrl || "https://attherate.dev/host-image/uploads/1760109986_ProductimgAI.png"}
        alt={logoData.altText || "Company Logo"}
        className={className}
      />
    );
  };

  return (
    <>
      <div className="offcanvas-panel">
        <div className="panel-overlay" />
        <div className="offcanvas-panel-inner">
          <div className="panel-logo">
            <Link to="/">
              {renderLogo()}
            </Link>
          </div>
          
          <div className="contact-us">
            <h5 className="panel-widget-title">Contact Us</h5>
            <ul>
              <li>
                <i className="fal fa-map-marker-alt" />
                121 King St, Melbourne VIC 3000, Australia.
              </li>
              <li>
                <i className="fal fa-envelope-open" />
                <a href="mailto:hello@barky.com"> hello@barky.com</a>
                <a href="mailto:info@barky.com">info@barky.com</a>
              </li>
              <li>
                <i className="fal fa-phone" />
                <a href="tel:(312)-895-9800">(312) 895-9800</a>
                <a href="tel:(312)-895-9888">(312) 895-9888</a>
              </li>
            </ul>
          </div>
          <a href="#" className="panel-close">
            <i className="fal fa-times" />
          </a>
        </div>
      </div>
      <header className="theme-header transparent-header">
        {/* header Navigation */}
        <div className="header-navigation navigation-style-v2">
          <div className="nav-overlay" />
          <div className="container-fluid">
            <div className="primary-menu">
              <div className="site-branding">
                <Link to="/" className="brand-logo">
                  {renderLogo()}
                </Link>
              </div>
              <div className="nav-menu nav-ml-auto">
                {/* Navbar Close */}
                <div className="navbar-close">
                  <i className="far fa-times" />
                </div>
             
                {/* Main Menu */}
                <nav className="main-menu">
                  <ul>
                    {/* Dashboard and Admin Links */}
                    {user ? (
                      <>
                        <li className="menu-item">
                          <Link to="/user/dashboard" className="nav-link">
                            Dashboard
                          </Link>
                        </li>
                        {userData?.userRole === "Admin" && (
                          <li className="menu-item">
                            <Link to="/admin/dashboard" className="nav-link">
                              Admin Panel
                            </Link>
                          </li>
                        )}
                      </>
                    ) : (
                      <li className="menu-item">
                        <Link to="/login" className="nav-link">
                          Login
                        </Link>
                      </li>
                    )}
                    
                  
                    
                    <li className="menu-item">
                       {user && (
                      <li className="menu-item">
                        <button 
                          onClick={handleSignout} 
                         
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          Sign Out
                        </button>
                      </li>
                    )}
                    </li>
                    
                    {/* Logout button for logged-in users */}
                  
                  </ul>
                </nav>
              </div>
              <div className="header-right-nav">
                <ul>
                  {/* User avatar and dropdown for logged-in users */}
                  {user && (
                    <li className="bar-item user-dropdown-container">
                      <button
                        className="user-avatar-button"
                        onClick={toggleUserDropdown}
                        aria-label="User menu"
                        aria-expanded={userDropdownOpen}
                        aria-haspopup="true"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <div className="user-avatar" style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: '#4a6cf7',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          {getUserInitials()}
                        </div>
                        <span>My Account</span>
                      </button>

                      {userDropdownOpen && (
                        <div className="user-dropdown" style={{
                          position: 'absolute',
                          top: 'calc(100% + 10px)',
                          right: '0',
                          width: '220px',
                          backgroundColor: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
                          zIndex: '1000',
                          overflow: 'hidden'
                        }} role="menu">
                          <div className="user-info" style={{
                            padding: '15px',
                            borderBottom: '1px solid #eee'
                          }}>
                            <div className="user-name" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                              {user?.displayName || "User"}
                            </div>
                            <div className="user-email" style={{ fontSize: '0.85rem', color: '#666' }}>
                              {user?.email}
                            </div>
                          </div>
                          <div className="user-dropdown-menu" style={{ padding: '5px 0' }}>
                            <Link
                              to="/user/dashboard"
                              className="dropdown-item"
                              onClick={closeUserDropdown}
                              role="menuitem"
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '10px 15px',
                                textAlign: 'left',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#333',
                                textDecoration: 'none',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              Dashboard
                            </Link>
                            {userData?.userRole === "Admin" && (
                              <Link
                                to="/admin/dashboard"
                                className="dropdown-item"
                                onClick={closeUserDropdown}
                                role="menuitem"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '10px 15px',
                                  textAlign: 'left',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: '#333',
                                  textDecoration: 'none',
                                  transition: 'background-color 0.2s'
                                }}
                              >
                                Admin Panel
                              </Link>
                            )}
                            <button
                              className="dropdown-item logout-button"
                              onClick={handleSignout}
                              role="menuitem"
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '10px 15px',
                                textAlign: 'left',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#e74c3c',
                                textDecoration: 'none',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              Sign Out
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  )}
                  
                  <li className="bar-item">
                   
                  </li>
                  <li className="navbar-toggle-btn">
                    <div className="navbar-toggler">
                      <span />
                      <span />
                      <span />
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </header>

      <style jsx>{`
        .header_right {
          position: relative;
        }

        .desktop-menu {
          display: block;
        }

        .desktop-menu ul {
          list-style: none;
          display: flex;
          margin: 0;
          padding: 0;
        }

        .desktop-menu ul li {
          margin-right: 20px;
        }

        .desktop-menu ul li:last-child {
          margin-right: 0;
        }

        .user-dropdown-container {
          position: relative;
        }

        .user-avatar-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background-color 0.3s;
        }

        .user-avatar-button:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: #4a6cf7;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }

        .user-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 220px;
          background-color: #fff;
          border-radius: 8px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          overflow: hidden;
        }

        .user-info {
          padding: 15px;
          border-bottom: 1px solid #eee;
        }

        .user-name {
          font-weight: bold;
          margin-bottom: 5px;
        }

        .user-email {
          font-size: 0.85rem;
          color: #666;
        }

        .user-dropdown-menu {
          padding: 5px 0;
        }

        .dropdown-item {
          display: block;
          width: 100%;
          padding: 10px 15px;
          text-align: left;
          background: none;
          border: none;
          cursor: pointer;
          color: #333;
          text-decoration: none;
          transition: background-color 0.2s;
        }

        .dropdown-item:hover {
          background-color: #f5f5f5;
        }

        .logout-button {
          color: #e74c3c;
        }

        .login-button {
          display: inline-block;
          padding: 8px 20px;
          background-color: #4a6cf7;
          color: white;
          border-radius: 4px;
          text-decoration: none;
          font-weight: 500;
          transition: background-color 0.3s;
        }

        .login-button:hover {
          background-color: #3a5bd9;
        }

        .mobile-menu-button {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 30px;
          height: 21px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          z-index: 1000;
          margin-left: auto;
        }

        .hamburger-line {
          width: 100%;
          height: 3px;
          background-color: #333;
          transition: all 0.3s ease;
        }

        .hamburger-line.open:nth-child(1) {
          transform: translateY(9px) rotate(45deg);
        }

        .hamburger-line.open:nth-child(2) {
          opacity: 0;
        }

        .hamburger-line.open:nth-child(3) {
          transform: translateY(-9px) rotate(-45deg);
        }

        .mobile-menu {
          position: absolute;
          top: 100%;
          right: 0;
          width: 250px;
          background-color: #fff;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          z-index: 999;
          padding: 20px;
          border-radius: 8px;
          margin-top: 0; /* Removed margin-top to eliminate gap */
        }

        .mobile-menu ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .mobile-menu ul li {
          margin-bottom: 10px;
        }

        .mobile-menu ul li:last-child {
          margin-bottom: 0;
        }

        .mobile-menu .nav-link {
          display: block;
          padding: 10px 0;
          color: #333;
          text-decoration: none;
        }

        .mobile-menu .logout-button {
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          padding: 10px 0;
          color: #e74c3c;
          cursor: pointer;
        }

        @media (max-width: 991px) {
          .desktop-menu {
            display: none;
          }

          .mobile-menu-button {
            display: flex;
          }

          .user-dropdown-container {
            display: none;
          }

          .mobile-menu {
            width: 100%;
            max-width: 300px;
          }
        }

        @media (max-width: 576px) {
          .mobile-menu {
            width: 100%;
            max-width: none;
            right: 0;
            left: 0;
            border-radius: 0;
          }
        }
      `}</style>
    </>
  );
};

export default NavBar;